import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import ThumbnailPanel from "./ThumbnailPanel";
import ImagePanel from "./ImagePanel";
import TranscriptionPanel from "./TranscriptionPanel";
import ControlsPanel from "./ControlsPanel";
import ManuscriptDetailCard from "./ManuscriptDetailCard";
import { useManuscripts } from "./ManuscriptContext";
import "./ManuscriptViewer.css";

const ManuscriptViewer = () => {
  const { id: manuscriptId, folioId } = useParams();
  const navigate = useNavigate();
  const { getManuscriptById, loading: contextLoading } = useManuscripts();
  const [currentPage, setCurrentPage] = useState(1);
  const [linesData, setLinesData] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageToFolioMap, setImageToFolioMap] = useState({});
  const [folioToImageMap, setFolioToImageMap] = useState({});

  // Shared states for panels and interactions
  const [activePanel, setActivePanel] = useState("image");
  const [showTranscriptionPanel, setShowTranscriptionPanel] = useState(true);
  const [showLineBoxes, setShowLineBoxes] = useState(true);
  const [showHoverText, setShowHoverText] = useState(true);
  const [showMetadataCard, setShowMetadataCard] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [hoveredLine, setHoveredLine] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);

  // Listen for window resize events
  useEffect(() => {
    let prevSmallScreen = window.innerWidth <= 1024;
    const handleResize = () => {
      const small = window.innerWidth <= 1024;
      setIsSmallScreen(small);
      if (small && !prevSmallScreen) {
        // Transitioned from large to small; force image panel
        setActivePanel("image");
      }
      prevSmallScreen = small;
    };
    window.addEventListener("resize", handleResize);
    handleResize(); // initial check
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle folio navigation through URL
  useEffect(() => {
    if (folioId && Object.keys(folioToImageMap).length > 0) {
      const normalizedFolioId = folioId.toLowerCase();
      const imageId = folioToImageMap[normalizedFolioId];
      
      if (imageId) {
        setCurrentPage(imageId);
      } else {
        // If folio doesn't exist, navigate to the first page
        navigate(`/viewer/${manuscriptId}`, { replace: true });
      }
    }
  }, [folioId, folioToImageMap, navigate, manuscriptId]);

  // Update URL when page changes
  useEffect(() => {
    if (currentPage && Object.keys(imageToFolioMap).length > 0) {
      const currentFolio = imageToFolioMap[currentPage];
      
      if (currentFolio) {
        if (!folioId || (folioId.toLowerCase() !== currentFolio.toLowerCase())) {
          navigate(`/viewer/${manuscriptId}/${currentFolio}`, { replace: true });
        }
      }
    }
  }, [currentPage, imageToFolioMap, manuscriptId, navigate, folioId]);

  useEffect(() => {
    if (!manuscriptId) {
      setError("No manuscript ID provided");
      setLoading(false);
      return;
    }

    if (!contextLoading) {
      // Get metadata from context
      const msData = getManuscriptById(manuscriptId);
      
      if (msData) {
        setMetadata({
          ...msData,
          total_images: parseInt(msData.total_images),
          total_folios: parseInt(msData.total_folios),
          start_folio: msData.start_folio,
        });
      } else {
        setError("Manuscript not found");
        setLoading(false);
        return;
      }

      // Load line data
      const loadLineData = async () => {
        try {
          const msDataResponse = await fetch(`/msdata/${manuscriptId}/ms_data.csv`);
          const msDataText = await msDataResponse.text();
          
          Papa.parse(msDataText, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const processedData = results.data.map((row) => ({
                ...row,
                line: parseInt(row.line, 10),
                start_x: parseFloat(row.start_x),
                start_y: parseFloat(row.start_y),
                end_x: parseFloat(row.end_x),
                end_y: parseFloat(row.end_y),
              }));
              
              // Extract image_id to folio mappings
              const imgToFolioMap = {};
              const folioToImgMap = {};
              
              // Create mapping between image_id and folio
              processedData.forEach(row => {
                if (row.image_id && row.folio) {
                  const imageId = parseInt(row.image_id);
                  const folio = row.folio;
                  
                  if (!imgToFolioMap[imageId]) {
                    imgToFolioMap[imageId] = folio;
                    folioToImgMap[folio.toLowerCase()] = imageId;
                  }
                }
              });
              
              setImageToFolioMap(imgToFolioMap);
              setFolioToImageMap(folioToImgMap);
              setLinesData(processedData);
              setLoading(false);
              
              // Handle initial folio navigation after data is loaded
              if (folioId && folioToImgMap[folioId.toLowerCase()]) {
                setCurrentPage(folioToImgMap[folioId.toLowerCase()]);
              }
            },
            error: (error) => {
              console.error("Parse error:", error);
              setError("Failed to parse manuscript data");
              setLoading(false);
            },
          });
        } catch (err) {
          console.error("Fetch error:", err);
          setError("Failed to load manuscript data");
          setLoading(false);
        }
      };

      loadLineData();
    }
  }, [manuscriptId, getManuscriptById, contextLoading, folioId]);

  // Utility to compute folio number for ThumbnailPanel
  const getFolioNumber = (pageNumber) => {
    return imageToFolioMap[pageNumber] || `Page ${pageNumber}`;
  };

  // Compute lines for the current page
  const getCurrentPageLines = () =>
    linesData
      .filter((line) => line.image_id === currentPage.toString())
      .sort((a, b) => a.line - b.line);

  // Toggle metadata card
  const toggleMetadataCard = () => {
    setShowMetadataCard(!showMetadataCard);
  };

  // Custom page setter that ensures URL updates
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  if (loading || contextLoading) {
    return (
      <div className="main">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading manuscript viewer...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center p10 highlight">{error}</div>;
  }

  const currentFolio = imageToFolioMap[currentPage] || "";

  return (
    <div className="manuscript-viewer">
      <div className="panels-container">
        <div className="panels-content">
          <ThumbnailPanel 
            metadata={metadata}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            getFolioNumber={getFolioNumber}
            manuscriptId={manuscriptId}
            totalImages={metadata.total_images}
          />
          {isSmallScreen ? (
            activePanel === "image" ? (
              <ImagePanel 
                metadata={metadata}
                manuscriptId={manuscriptId}
                currentPage={currentPage}
                zoom={zoom}
                setZoom={setZoom}
                offset={offset}
                setOffset={setOffset}
                hoveredLine={hoveredLine}
                setHoveredLine={setHoveredLine}
                showLineBoxes={showLineBoxes}
                showHoverText={showHoverText}
                lines={getCurrentPageLines()}
              />
            ) : (
              <TranscriptionPanel 
                lines={getCurrentPageLines()}
                hoveredLine={hoveredLine}
                setHoveredLine={setHoveredLine}
              />
            )
          ) : (
            <>
              <ImagePanel 
                metadata={metadata}
                manuscriptId={manuscriptId}
                currentPage={currentPage}
                zoom={zoom}
                setZoom={setZoom}
                offset={offset}
                setOffset={setOffset}
                hoveredLine={hoveredLine}
                setHoveredLine={setHoveredLine}
                showLineBoxes={showLineBoxes}
                showHoverText={showHoverText}
                lines={getCurrentPageLines()}
                fullWidth={!showTranscriptionPanel}
              />
              {showTranscriptionPanel && (
                <TranscriptionPanel 
                  lines={getCurrentPageLines()}
                  hoveredLine={hoveredLine}
                  setHoveredLine={setHoveredLine}
                />
              )}
            </>
          )}

          {showMetadataCard && (
            <ManuscriptDetailCard 
              metadata={metadata} 
              onClose={toggleMetadataCard}
            />
          )}
        </div>
        <ControlsPanel 
          currentPage={currentPage}
          setCurrentPage={handlePageChange}
          totalImages={metadata.total_images}
          currentFolio={currentFolio}
          copyright={metadata.copyright}
          showLineBoxes={showLineBoxes}
          setShowLineBoxes={setShowLineBoxes}
          showHoverText={showHoverText}
          setShowHoverText={setShowHoverText}
          activePanel={activePanel}
          setActivePanel={setActivePanel}
          showTranscriptionPanel={showTranscriptionPanel}
          setShowTranscriptionPanel={setShowTranscriptionPanel}
          isSmallScreen={isSmallScreen}
          showMetadataCard={showMetadataCard}
          toggleMetadataCard={toggleMetadataCard}
        />
      </div>
    </div>
  );
};

export default ManuscriptViewer;